/**
 * La police de thème du lecteur de forme (#1862) : un fichier Word peut ne
 * nommer aucune police et renvoyer à celles de son thème (`w:asciiTheme=
 * "minorHAnsi"`), dont le nom vit dans `word/theme/theme1.xml`.
 *
 * Mesuré sur SuperDoc 2.15.1 (2026-10-02, Word importé de référence) :
 * - l'éditeur peint la police latine du thème (`a:latin`) — majeure pour
 *   `major*`, mineure pour `minor*` ;
 * - il ne lit que l'emplacement `ascii` : `hAnsiTheme`, `eastAsiaTheme` et
 *   `cstheme` seuls ne changent pas la police peinte ;
 * - ⚠️ quand un élément porte `ascii` ET `asciiTheme`, l'éditeur peint la
 *   police NOMMÉE alors que la norme fait ignorer `ascii` au profit du thème.
 *   Si les deux noms diffèrent, aucun n'est sûr : le réglage est rendu non
 *   lisible.
 *
 * Source de la règle normative : ECMA-376 Part 1 §17.3.2.26 `rFonts`,
 * https://learn.microsoft.com/en-us/openspecs/office_standards/ms-oi29500/aef3c9a6-5d6c-434b-90b7-85e761fd8e62
 * (vérifié 2026-10-02).
 */

import type { XmlNode } from '@hector/docx-integrity';
import { attribut, enfant, type Lecture } from './cascadeDeForme';

/** Les deux polices latines du thème. Absente : le fichier n'a pas de thème, ou le thème ne la nomme pas. */
export interface PolicesDeTheme {
  majeure: string | undefined;
  mineure: string | undefined;
}

const ESPACES_DE_DESSIN: readonly string[] = [
  'http://schemas.openxmlformats.org/drawingml/2006/main',
  'http://purl.oclc.org/ooxml/drawingml/main',
];

function enfantDeDessin(noeud: XmlNode | undefined, nom: string): XmlNode | undefined {
  return noeud?.children.find((candidat) => candidat.local === nom && ESPACES_DE_DESSIN.includes(candidat.uri));
}

/**
 * Lit les polices latines du thème.
 *
 * @param racine La racine de `word/theme/theme1.xml`, ou `undefined` si le fichier n'a pas de thème.
 */
export function lireLesPolicesDuTheme(racine: XmlNode | undefined): PolicesDeTheme {
  const polices = enfantDeDessin(enfantDeDessin(racine, 'themeElements'), 'fontScheme');
  // `typeface` est un attribut sans préfixe : il n'a pas d'espace de noms. Vide, il ne nomme rien.
  const latine = (groupe: string): string | undefined => enfantDeDessin(enfantDeDessin(polices, groupe), 'latin')?.attributes['{}typeface'] || undefined;
  return { majeure: latine('majorFont'), mineure: latine('minorFont') };
}

/**
 * Les jetons de `w:asciiTheme` dont la police peinte a été mesurée. Les autres
 * (`majorAscii`, `minorAscii`, `*EastAsia`, `*Bidi`) rendent la police non lisible.
 */
const POLICE_DU_JETON: Readonly<Record<string, keyof PolicesDeTheme>> = {
  majorHAnsi: 'majeure',
  minorHAnsi: 'mineure',
};

/**
 * La lecture de la police d'un passage (`w:rFonts`), police de thème résolue.
 *
 * @param theme Les polices du thème du fichier lu.
 */
export function lectureDeLaPolice(theme: PolicesDeTheme): Lecture<string> {
  return (proprietes) => {
    const noeud = enfant(proprietes, 'rFonts');
    if (!noeud) return undefined;
    const nommee = attribut(noeud, 'ascii');
    const jeton = attribut(noeud, 'asciiTheme');
    if (jeton === undefined) return nommee ? { valeur: nommee } : undefined;
    const emplacement = POLICE_DU_JETON[jeton];
    const duTheme = emplacement === undefined ? undefined : theme[emplacement];
    if (duTheme === undefined || (nommee !== undefined && nommee !== duTheme)) return { nonLisible: 'police_de_theme' };
    return { valeur: duTheme };
  };
}
